import React, { FC, PropsWithChildren } from 'react'
import NProgress from 'nprogress'
import Router from 'next/router'
import WindowListener from 'components/Screen'
import TakeoverIndicator from 'components/Header/TakeoverIndicator'
import FlashMessage from 'components/FlashMessage'
import LayoutConnector, { ConnectorProps as LayoutProps } from 'containers/Layout'
import StyleBase from 'components/StyleBase'
import Loader from 'components/Loader'
import { AppLayout } from 'styles/layout'
import LogStore from 'components/LogStore'
import SkipToContent from 'components/Header/SkipToContent'
import GlobalModals from 'layouts/Modals'
import { Footer } from 'layouts/Dynamic'
import { NavigationProvider, useNavigation } from './NavigationV2/NavigationContext'
import Header from './NavigationV2/Header/Header'
import Sidebar from './NavigationV2/Sidebar/Sidebar'
import MobileQuickLinks from './NavigationV2/MobileQuickLinks/MobileQuickLinks'
import styles from './index.module.scss'
import classNames from 'classnames'

const { APP_ENV } = process.env

NProgress.configure({
  easing: 'ease',
  speed: 500,
  showSpinner: true,
  trickleSpeed: 300,
})

Router.onRouteChangeStart = () => NProgress.start()
Router.onRouteChangeComplete = () => NProgress.done()
Router.onRouteChangeError = () => NProgress.done()

type StudioLayoutProps = LayoutProps & {
  disableHeader?: boolean
  disableFooter?: boolean
  siteContext?: string
  dataApp: string
  customClassName?: string
}

const StudioNavigation: FC = () => {
  const {
    fullName,
    activeHref,
    isMobileNavOpen,
    onToggleMobileNav,
    isDesktopCollapsed,
    onToggleDesktopCollapse,
  } = useNavigation()

  return (
    <div className={styles.navigationV2} data-section='studio-navigation-v2'>
      <Header
        fullName={fullName}
        isMobileNavOpen={isMobileNavOpen}
        onToggleMobileNav={onToggleMobileNav}
        isDesktopCollapsed={isDesktopCollapsed}
        onToggleDesktopCollapse={onToggleDesktopCollapse}
      />
      <TakeoverIndicator customStyle={styles.takeoverIndicator} />

      <MobileQuickLinks activeHref={activeHref} />

      <Sidebar fullName={fullName} />
    </div>
  )
}

type LayoutContentProps = PropsWithChildren<{
  dataApp: string
  siteContext?: string
  isReady?: boolean
  disableFooter?: boolean
  customClassName?: string
}>

const LayoutContent: FC<LayoutContentProps> = ({
  children,
  dataApp,
  siteContext,
  isReady,
  disableFooter,
  customClassName,
}) => {
  const { isDesktopCollapsed } = useNavigation()

  return (
    <div
      className={`${styles.studioContentRow} ${
        isDesktopCollapsed ? styles.studioContentRowCollapsed : ''
      }`}
      data-studio-menu={isDesktopCollapsed ? 'collapsed' : 'expanded'}
    >
      <div className={styles.contentPane}>
        <AppLayout
          className={classNames(styles.studioAppLayout, customClassName)}
          data-app={dataApp || 'app'}
          data-sitecontext={siteContext}
          {...(!isReady
            ? {
                [`data-loading`]: isReady,
              }
            : {})}
        >
          <Loader />
          {children}
        </AppLayout>
      </div>

      {!disableFooter && (
        <div className={styles.studioFooterWrapper} data-studio-footer>
          <Footer isStudio={true} />
        </div>
      )}
    </div>
  )
}

const StudioLayout: FC<PropsWithChildren<StudioLayoutProps>> = (props) => {
  const {
    children,
    dataApp,
    disableFooter,
    disableHeader,
    isReady,
    siteContext,
    customClassName,
  } = props

  return (
    <StyleBase>
      <NavigationProvider>
        <WindowListener />
        <FlashMessage />
        <SkipToContent skipTo='[data-app]' />

        {!disableHeader && <StudioNavigation />}

        <LayoutContent
          customClassName={customClassName}
          dataApp={dataApp}
          siteContext={siteContext}
          isReady={isReady}
          disableFooter={disableFooter}
        >
          {children}
        </LayoutContent>

        {APP_ENV === 'local' && <LogStore />}
        <GlobalModals />
      </NavigationProvider>
    </StyleBase>
  )
}

export default LayoutConnector(StudioLayout)
